import { useMemo } from "react"
import { useSelector } from "react-redux"
import { merchantSelectors } from "../store/merchantReducer"
import { providerSelectors } from "../store/providerSlice"
import { SharedStoreState } from "../store/SharedStoreState"
import { TPayment } from "../types/payment/TPayment"
import { EProviderName, IProviderConfig } from "../types/provider/IProvider"
import { getDefaultProviderForPaymentType } from "./paymentService"

export function useProviderForPaymentTypeWhenDataLoaded(
    paymentType: TPayment,
    webshopId: string | undefined
): EProviderName | undefined {
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const providerConfigs = useSelector((state: SharedStoreState) =>
        providerSelectors.configs(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )
    if (merchantDetails === undefined || providerConfigs === undefined) {
        return undefined
    }
    return getConfiguredProviderForPaymentType(
        providerConfigs ?? [],
        webshopId,
        paymentType,
        merchantDetails?.swMerchant.countryCode ?? ""
    )
}

export function useProviderForPaymentType(paymentType: TPayment, webshopId: string | undefined): EProviderName {
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const providerConfigs = useSelector((state: SharedStoreState) =>
        providerSelectors.configs(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )
    return getConfiguredProviderForPaymentType(
        providerConfigs ?? [],
        webshopId,
        paymentType,
        merchantDetails?.swMerchant.countryCode ?? ""
    )
}

// Will first try to find a webshop specific config, and if not found, will fall back to merchant level config
function getPaymentConfigForPaymentType(
    configs: IProviderConfig[],
    webshopId: string | undefined,
    paymentType: TPayment
): IProviderConfig | undefined {
    const merchantConfigs = configs?.filter((c) => c.type === "Payment")
    const paymentConfig = merchantConfigs?.find(
        (c) => c.paymentType === paymentType && (webshopId ? c.webshopId === webshopId : !c.webshopId)
    )

    if (paymentConfig) {
        return paymentConfig
    }

    return merchantConfigs?.find((c) => c.paymentType === paymentType && !c.webshopId)
}

export function getConfiguredProviderForPaymentType(
    configs: IProviderConfig[],
    webshopId: string | undefined,
    paymentType: TPayment,
    countryCode: string
): EProviderName {
    const paymentConfig = getPaymentConfigForPaymentType(configs, webshopId, paymentType)

    if (paymentConfig?.preferredProvider) {
        return paymentConfig.preferredProvider
    }

    return getDefaultProviderForPaymentType(countryCode, paymentType)
}

export type IConfiguredPaymentProviders = { [paymentType in TPayment]?: EProviderName }

export function useConfiguredPaymentProviders(webshopId: string | undefined) {
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const providerConfigs = useSelector((state: SharedStoreState) =>
        providerSelectors.configs(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )
    const providerMapping = useMemo<IConfiguredPaymentProviders>(
        () => ({
            Vipps: getConfiguredProviderForPaymentType(
                providerConfigs ?? [],
                webshopId,
                "Vipps",
                merchantDetails?.swMerchant.countryCode ?? ""
            ),
        }),
        [providerConfigs, webshopId, merchantDetails]
    )

    if (!merchantDetails || !providerConfigs) {
        return undefined
    }

    return providerMapping
}
